import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  parseISO,
  format,
  differenceInDays,
  addDays,
  isValid,
  startOfDay,
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Loader2,
  Factory,
  CalendarX,
  X,
  LocateFixed,
  ZoomIn,
  ZoomOut,
  Search,
} from 'lucide-react';

const BASE_DAY_WIDTH = 48; // pixels per day at zoom=1
const LEFT_COL_WIDTH = 280; // pixels
const ROW_HEIGHT = 88; // pixels
const HEADER_HEIGHT = 40; // pixels
const MONTH_HEADER_HEIGHT = 22; // pixels
const STORAGE_KEY = 'production-gantt-selected-statuses';

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  if (typeof value === 'string') {
    let d = parseISO(value);
    if (isValid(d)) return d;
    d = new Date(value);
    return isValid(d) ? d : null;
  }
  return null;
}

function getStatusConfig(status) {
  const map = {
    draft: {
      label: '草稿',
      variant: 'secondary',
      barBg: 'bg-slate-200',
      barFill: 'bg-slate-500',
      text: 'text-slate-700',
    },
    released: {
      label: '已发布',
      variant: 'default',
      barBg: 'bg-blue-200',
      barFill: 'bg-blue-500',
      text: 'text-blue-700',
    },
    in_progress: {
      label: '生产中',
      variant: 'warning',
      barBg: 'bg-amber-200',
      barFill: 'bg-amber-500',
      text: 'text-amber-700',
    },
    completed: {
      label: '已完成',
      variant: 'success',
      barBg: 'bg-green-200',
      barFill: 'bg-green-500',
      text: 'text-green-700',
    },
    cancelled: {
      label: '已取消',
      variant: 'destructive',
      barBg: 'bg-red-200',
      barFill: 'bg-red-400',
      text: 'text-red-700',
    },
  };
  return (
    map[status] || {
      label: status,
      variant: 'secondary',
      barBg: 'bg-gray-200',
      barFill: 'bg-gray-400',
      text: 'text-gray-700',
    }
  );
}

function getPriorityConfig(priority) {
  const map = {
    low: { label: '低', variant: 'outline' },
    normal: { label: '普通', variant: 'secondary' },
    high: { label: '高', variant: 'default' },
    urgent: { label: '紧急', variant: 'destructive' },
  };
  return map[priority] || { label: priority, variant: 'secondary' };
}

function getProductLabel(order) {
  if (order.items && order.items.length > 0) {
    const names = order.items
      .slice(0, 2)
      .map((i) => i.finished_product_code || i.finished_product_name)
      .filter(Boolean);
    if (order.items.length > 2) {
      names.push(`+${order.items.length - 2}`);
    }
    return names.join(', ') || '未指定产品';
  }
  return order.finished_product_code || order.finished_product_name || '未指定产品';
}

function getQuantities(order) {
  const planned =
    order.total_planned_quantity ??
    order.planned_quantity ??
    (order.items
      ? order.items.reduce((s, i) => s + (i.planned_quantity || 0), 0)
      : 0);
  const completed =
    order.total_completed_quantity ??
    order.completed_quantity ??
    (order.items
      ? order.items.reduce((s, i) => s + (i.completed_quantity || 0), 0)
      : 0);
  const scrap = order.scrap_quantity || 0;
  return { planned, completed, scrap };
}

const ALL_STATUSES = [
  { key: 'draft', label: '草稿', dot: 'bg-slate-500', activeBg: 'bg-slate-500' },
  { key: 'released', label: '已发布', dot: 'bg-blue-500', activeBg: 'bg-blue-500' },
  { key: 'in_progress', label: '生产中', dot: 'bg-amber-500', activeBg: 'bg-amber-500' },
  { key: 'completed', label: '已完成', dot: 'bg-green-500', activeBg: 'bg-green-500' },
  { key: 'cancelled', label: '已取消', dot: 'bg-red-400', activeBg: 'bg-red-400' },
];

export default function ProductionGanttChart({ orders, onViewOrder, loading }) {
  // 1. Status filter with localStorage persistence
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter((s) => ALL_STATUSES.some((st) => st.key === s));
        }
      }
    } catch {
      // ignore
    }
    return ALL_STATUSES.map((s) => s.key);
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  const toggleStatus = (key) => {
    setSelectedStatuses((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  };

  // 2. Zoom control
  const [zoom, setZoom] = useState(1);
  const dayWidth = BASE_DAY_WIDTH * zoom;
  const zoomIn = () =>
    setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100));
  const zoomOut = () =>
    setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100));
  const resetZoom = () => setZoom(1);

  // 3. Search
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef(null);
  const searchLower = searchQuery.trim().toLowerCase();

  const isSearchMatch = (order) => {
    if (!searchLower) return false;
    const text = [
      order.production_order_id,
      getProductLabel(order),
      order.client_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return text.includes(searchLower);
  };

  // 4. Sort orders by planned start date (ascending), unscheduled at bottom
  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const da = parseDate(a.planned_start_date || a.actual_start_date);
      const db = parseDate(b.planned_start_date || b.actual_start_date);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.getTime() - db.getTime();
    });
  }, [orders]);

  const filteredOrders = useMemo(
    () => sortedOrders.filter((o) => selectedStatuses.includes(o.status)),
    [sortedOrders, selectedStatuses]
  );

  // 5. Hover sync between left column and right timeline
  const [hoveredRowId, setHoveredRowId] = useState(null);

  // 6. Timeline calculations
  const { timelineStart, totalDays } = useMemo(() => {
    const allDates = filteredOrders
      .flatMap((o) => [
        o.planned_start_date,
        o.planned_end_date,
        o.actual_start_date,
        o.actual_end_date,
      ])
      .filter(Boolean)
      .map(parseDate)
      .filter(isValid);

    if (allDates.length === 0) {
      const today = startOfDay(new Date());
      return { timelineStart: today, timelineEnd: addDays(today, 7), totalDays: 8 };
    }

    const min = new Date(Math.min(...allDates));
    const max = new Date(Math.max(...allDates));
    const start = startOfDay(addDays(min, -1));
    const end = startOfDay(addDays(max, 2));
    const days = differenceInDays(end, start) + 1;
    return { timelineStart: start, timelineEnd: end, totalDays: days };
  }, [filteredOrders]);

  const todayOffset = useMemo(() => {
    const today = startOfDay(new Date());
    const days = differenceInDays(today, timelineStart);
    return days >= 0 && days < totalDays ? days * dayWidth : null;
  }, [timelineStart, totalDays, dayWidth]);

  const timelineWidth = totalDays * dayWidth;

  const dateTicks = useMemo(() => {
    const ticks = [];
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(timelineStart, i);
      ticks.push({
        date: d,
        label: format(d, 'MM/dd', { locale: zhCN }),
        weekday: format(d, 'EEE', { locale: zhCN }),
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
      });
    }
    return ticks;
  }, [timelineStart, totalDays]);

  // 7. Month grouping for month header row
  const monthGroups = useMemo(() => {
    const groups = [];
    let currentMonth = null;
    let startIdx = 0;
    dateTicks.forEach((tick, idx) => {
      const monthKey = format(tick.date, 'yyyy-MM');
      if (monthKey !== currentMonth) {
        if (currentMonth !== null) {
          groups.push({
            label: format(addDays(timelineStart, startIdx), 'yyyy年M月', { locale: zhCN }),
            startIdx,
            count: idx - startIdx,
          });
        }
        currentMonth = monthKey;
        startIdx = idx;
      }
    });
    if (currentMonth !== null) {
      groups.push({
        label: format(addDays(timelineStart, startIdx), 'yyyy年M月', { locale: zhCN }),
        startIdx,
        count: dateTicks.length - startIdx,
      });
    }
    return groups;
  }, [dateTicks, timelineStart]);

  // 8. Scroll to today
  const scrollToToday = () => {
    if (scrollRef.current && todayOffset !== null) {
      const container = scrollRef.current;
      const targetLeft = todayOffset - container.clientWidth / 2 + dayWidth / 2;
      container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
    }
  };

  // 9. Auto-scroll to first search match
  useEffect(() => {
    if (!searchLower || !scrollRef.current || filteredOrders.length === 0) return;
    const firstMatch = filteredOrders.find((o) => {
      const text = [
        o.production_order_id,
        getProductLabel(o),
        o.client_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return text.includes(searchLower);
    });
    if (firstMatch) {
      const start = parseDate(
        firstMatch.planned_start_date || firstMatch.actual_start_date
      );
      if (isValid(start)) {
        const offsetDays = differenceInDays(startOfDay(start), timelineStart);
        const targetLeft = offsetDays * dayWidth - 100;
        scrollRef.current.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'smooth',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filteredOrders, timelineStart, dayWidth]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12 text-muted-foreground">
          未找到生产工单。
        </CardContent>
      </Card>
    );
  }

  const isRowHovered = (orderId) => hoveredRowId === orderId;

  const getRowBgClass = (order) => {
    const match = isSearchMatch(order);
    const hover = isRowHovered(order._id);
    if (match && hover) return 'bg-amber-100';
    if (match) return 'bg-amber-50';
    if (hover) return 'bg-muted/60';
    return '';
  };

  const getRightRowBgClass = (order) => {
    const match = isSearchMatch(order);
    const hover = isRowHovered(order._id);
    if (match && hover) return 'bg-amber-100/80';
    if (match) return 'bg-amber-50/80';
    if (hover) return 'bg-muted/60';
    return '';
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Factory className="h-5 w-5" />
              生产排期甘特图
              <span className="text-sm text-muted-foreground font-normal">
                ({filteredOrders.length} / {orders.length} 个工单)
              </span>
            </CardTitle>
            <div className="flex items-center gap-1.5">
              {/* Zoom controls */}
              <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
                <button
                  onClick={zoomOut}
                  disabled={zoom <= 0.5}
                  className="inline-flex items-center justify-center px-2 py-1 text-xs hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  title="缩小"
                >
                  <ZoomOut className="h-3 w-3" />
                </button>
                <span className="px-2 py-1 text-[10px] font-medium text-muted-foreground border-x border-border min-w-[40px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  disabled={zoom >= 3}
                  className="inline-flex items-center justify-center px-2 py-1 text-xs hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  title="放大"
                >
                  <ZoomIn className="h-3 w-3" />
                </button>
              </div>
              {zoom !== 1 && (
                <button
                  onClick={resetZoom}
                  className="inline-flex items-center rounded-md border border-border px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
                >
                  重置
                </button>
              )}
              {todayOffset !== null && (
                <button
                  onClick={scrollToToday}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
                  title="跳转到今天"
                >
                  <LocateFixed className="h-3 w-3" />
                  今天
                </button>
              )}
            </div>
          </div>

          {/* Filters + Search */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {ALL_STATUSES.map((status) => {
              const isActive = selectedStatuses.includes(status.key);
              return (
                <button
                  key={status.key}
                  onClick={() => toggleStatus(status.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors cursor-pointer ${
                    isActive
                      ? `border-transparent text-white ${status.activeBg}`
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${isActive ? 'bg-white' : status.dot}`}
                  />
                  <span>{status.label}</span>
                  {isActive && <X className="h-3 w-3" />}
                </button>
              );
            })}
            {selectedStatuses.length < ALL_STATUSES.length && (
              <button
                onClick={() =>
                  setSelectedStatuses(ALL_STATUSES.map((s) => s.key))
                }
                className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                重置
              </button>
            )}
            <div className="flex-1 min-w-[20px]" />
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="搜索工单号、产品、客户…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 pr-7 h-7 text-xs w-52"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border-t">
              <CalendarX className="h-8 w-8 mb-3 opacity-50" />
              <p className="text-sm">当前筛选条件下没有工单</p>
              <button
                onClick={() => setSelectedStatuses(ALL_STATUSES.map((s) => s.key))}
                className="text-primary text-sm mt-2 hover:underline cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <div className="flex border-t">
              {/* Left fixed column */}
              <div
                className="flex-shrink-0 border-r bg-muted/30"
                style={{ width: LEFT_COL_WIDTH }}
              >
                {/* Month + Date headers placeholder for alignment */}
                <div style={{ height: MONTH_HEADER_HEIGHT }} className="border-b" />
                {/* Header */}
                <div
                  className="flex items-end px-3 pb-2 border-b font-medium text-xs text-muted-foreground"
                  style={{ height: HEADER_HEIGHT }}
                >
                  工单 / 产品
                </div>
                {/* Rows */}
                {filteredOrders.map((order) => {
                  const statusCfg = getStatusConfig(order.status);
                  const priCfg = getPriorityConfig(order.priority);
                  const bgClass = getRowBgClass(order);
                  return (
                    <div
                      key={order._id}
                      className={`border-b px-3 py-2 flex flex-col justify-center cursor-pointer transition-colors ${bgClass || 'hover:bg-muted/50'}`}
                      style={{ height: ROW_HEIGHT }}
                      onClick={() => onViewOrder(order._id)}
                      onMouseEnter={() => setHoveredRowId(order._id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground truncate">
                          {order.production_order_id}
                        </span>
                        <Badge variant={priCfg.variant} className="text-[10px] px-1 py-0 h-4">
                          {priCfg.label}
                        </Badge>
                      </div>
                      <div className="text-sm truncate mt-0.5">{getProductLabel(order)}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant={statusCfg.variant} className="text-[10px] px-1 py-0 h-4">
                          {statusCfg.label}
                        </Badge>
                        {order.client_name && (
                          <span className="text-[10px] text-muted-foreground truncate">
                            {order.client_name}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right timeline scroll area */}
              <div className="flex-1 overflow-x-auto relative" ref={scrollRef}>
                <div style={{ minWidth: timelineWidth, position: 'relative' }}>
                  {/* Month header row */}
                  <div
                    className="flex border-b bg-muted/30"
                    style={{ height: MONTH_HEADER_HEIGHT }}
                  >
                    {monthGroups.map((m, i) => (
                      <div
                        key={i}
                        className="flex-shrink-0 flex items-center justify-center text-[10px] font-medium text-muted-foreground border-r"
                        style={{ width: m.count * dayWidth }}
                      >
                        {m.label}
                      </div>
                    ))}
                  </div>

                  {/* Date header */}
                  <div
                    className="flex border-b bg-muted/20"
                    style={{ height: HEADER_HEIGHT }}
                  >
                    {dateTicks.map((tick, idx) => (
                      <div
                        key={idx}
                        className={`flex-shrink-0 flex flex-col items-center justify-end pb-1 text-[10px] border-r ${
                          tick.isWeekend ? 'bg-muted/40' : ''
                        }`}
                        style={{ width: dayWidth }}
                      >
                        <span className="text-muted-foreground">{tick.weekday}</span>
                        <span className="font-medium">{tick.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Rows */}
                  {filteredOrders.map((order) => {
                    const start = parseDate(
                      order.planned_start_date || order.actual_start_date
                    );
                    const end = parseDate(order.planned_end_date || order.actual_end_date);
                    const actualStart = parseDate(order.actual_start_date);
                    const actualEnd = parseDate(order.actual_end_date);
                    const statusCfg = getStatusConfig(order.status);
                    const { planned, completed } = getQuantities(order);
                    const progress = planned > 0 ? Math.min(100, Math.round((completed / planned) * 100)) : 0;

                    let barLeft = 0;
                    let barWidth = 0;
                    let hasBar = false;

                    if (isValid(start) && isValid(end)) {
                      const offsetDays = differenceInDays(startOfDay(start), timelineStart);
                      const durationDays = Math.max(1, differenceInDays(startOfDay(end), startOfDay(start)));
                      barLeft = offsetDays * dayWidth;
                      barWidth = durationDays * dayWidth;
                      hasBar = true;
                    }

                    let actualBarLeft = 0;
                    let actualBarWidth = 0;
                    let hasActualBar = false;
                    if (isValid(actualStart) && isValid(actualEnd)) {
                      const offsetDays = differenceInDays(startOfDay(actualStart), timelineStart);
                      const durationDays = Math.max(1, differenceInDays(startOfDay(actualEnd), startOfDay(actualStart)));
                      actualBarLeft = offsetDays * dayWidth;
                      actualBarWidth = durationDays * dayWidth;
                      hasActualBar = true;
                    }

                    const rowBg = getRightRowBgClass(order);

                    return (
                      <div
                        key={order._id}
                        className={`relative border-b transition-colors cursor-pointer ${rowBg || 'hover:bg-muted/30'}`}
                        style={{ height: ROW_HEIGHT }}
                        onClick={() => onViewOrder(order._id)}
                        onMouseEnter={() => setHoveredRowId(order._id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                      >
                        {/* Grid lines */}
                        <div className="absolute inset-0 flex">
                          {dateTicks.map((_, idx) => (
                            <div
                              key={idx}
                              className="flex-shrink-0 border-r border-dashed border-border/50"
                              style={{ width: dayWidth }}
                            />
                          ))}
                        </div>

                        {/* Planned bar */}
                        {hasBar ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={`absolute rounded-md overflow-hidden cursor-pointer shadow-sm ${statusCfg.barBg} ${statusCfg.text}`}
                                style={{
                                  left: barLeft,
                                  width: Math.max(barWidth, 4),
                                  height: 28,
                                  top: 'calc(50% - 20px)',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewOrder(order._id);
                                }}
                              >
                                {/* Progress fill */}
                                <div
                                  className={`h-full ${statusCfg.barFill} opacity-80`}
                                  style={{ width: `${progress}%` }}
                                />
                                {/* Label inside bar if wide enough */}
                                {barWidth > 60 && (
                                  <div className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-white mix-blend-difference">
                                    {progress}%
                                  </div>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="space-y-1">
                                <p className="font-medium">{order.production_order_id}</p>
                                <p className="text-xs text-muted-foreground">
                                  {getProductLabel(order)}
                                </p>
                                <div className="text-xs space-y-0.5 pt-1">
                                  <p>
                                    计划:{' '}
                                    {order.planned_start_date
                                      ? format(parseDate(order.planned_start_date), 'yyyy-MM-dd')
                                      : '-'}{' '}
                                    ~{' '}
                                    {order.planned_end_date
                                      ? format(parseDate(order.planned_end_date), 'yyyy-MM-dd')
                                      : '-'}
                                  </p>
                                  {(order.actual_start_date || order.actual_end_date) && (
                                    <p>
                                      实际:{' '}
                                      {order.actual_start_date
                                        ? format(parseDate(order.actual_start_date), 'yyyy-MM-dd')
                                        : '-'}{' '}
                                      ~{' '}
                                      {order.actual_end_date
                                        ? format(parseDate(order.actual_end_date), 'yyyy-MM-dd')
                                        : '-'}
                                    </p>
                                  )}
                                  <p>
                                    进度: {completed} / {planned} ({progress}%)
                                  </p>
                                  {order.scrap_quantity > 0 && (
                                    <p className="text-destructive">
                                      报废: {order.scrap_quantity}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div className="absolute inset-0 flex items-center px-4">
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <CalendarX className="h-3 w-3" />
                              未排期
                            </span>
                          </div>
                        )}

                        {/* Actual time indicator bar */}
                        {hasActualBar && (
                          <div
                            className="absolute rounded-sm bg-gray-700/80"
                            style={{
                              left: actualBarLeft,
                              width: Math.max(actualBarWidth, 4),
                              height: 3,
                              top: 'calc(50% + 10px)',
                            }}
                            title={`实际: ${format(actualStart, 'yyyy-MM-dd')} ~ ${format(actualEnd, 'yyyy-MM-dd')}`}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Today line */}
                  {todayOffset !== null && (
                    <div
                      className="absolute top-0 bottom-0 pointer-events-none"
                      style={{ left: todayOffset }}
                    >
                      <div className="w-px h-full bg-red-500/60 border-l border-dashed border-red-500" />
                      <div className="absolute -top-0 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[9px] px-1 py-0 rounded">
                        今天
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
